import { z } from 'zod';

/** mirrors 03-database-spec.md §4.19 material_inventory_movements + §4.20 monthly_inventory_snapshots */

export const inventoryMovementTypeValues = [
  'opening',
  'production',
  'shipment',
  'secondary_transfer',
  'adjustment_in',
  'adjustment_out',
  'loss',
  'closing_snapshot',
] as const;
export type InventoryMovementType = (typeof inventoryMovementTypeValues)[number];

/** IN/OUT bucket inferred from `movement_type` for filter pills / labels. */
export const movementBucket = (mt: string): 'in' | 'out' | 'unknown' => {
  if (mt === 'opening' || mt === 'production' || mt === 'adjustment_in') return 'in';
  if (mt === 'shipment' || mt === 'secondary_transfer' || mt === 'adjustment_out' || mt === 'loss')
    return 'out';
  return 'unknown';
};

export const inventoryMovementSchema = z.object({
  inventory_movement_id: z.string(),
  monthly_batch_id: z.string(),
  material_id: z.string(),
  movement_type: z.enum(inventoryMovementTypeValues),
  movement_date: z.string(),
  kg_delta: z.string(),
  source_record_type: z.string().nullable().optional(),
  source_record_id: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
});

export type InventoryMovementDto = z.infer<typeof inventoryMovementSchema>;

/** Aggregated inventory row returned by the legacy GET /api/monthly-batches/:id/inventory */
export const inventoryAggregateSchema = z.object({
  material_id: z.string(),
  material_name: z.string(),
  opening_kg: z.string(),
  production_kg: z.string(),
  shipment_kg: z.string(),
  adjustment_kg: z.string(),
  closing_kg: z.string(),
});

export type InventoryAggregateDto = z.infer<typeof inventoryAggregateSchema>;

/** Per-material IN/OUT summary returned by GET /inventory/summary. */
export const inventorySummaryRowSchema = z.object({
  material_id: z.string(),
  material_name: z.string(),
  material_type: z.string(),
  opening_kg: z.string(),
  in_kg: z.string(),
  out_kg: z.string(),
  current_kg: z.string(),
  last_movement_at: z.string().nullable().optional(),
});
export type InventorySummaryRow = z.infer<typeof inventorySummaryRowSchema>;

/** Movement log row returned by GET /inventory/movements (joined names). */
export const inventoryMovementLogRowSchema = z.object({
  inventory_movement_id: z.string(),
  monthly_batch_id: z.string(),
  material_id: z.string(),
  material_name: z.string(),
  movement_type: z.enum(inventoryMovementTypeValues),
  movement_date: z.string(),
  kg_delta: z.string(),
  source_record_type: z.string().nullable().optional(),
  source_record_id: z.string().nullable().optional(),
  approval_id: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
  created_by_name: z.string().nullable().optional(),
  created_at: z.string(),
});
export type InventoryMovementLogRow = z.infer<typeof inventoryMovementLogRowSchema>;

/** Legacy request shape converted to an approval proposal by /inventory-movements. */
export const adjustmentCreateSchema = z.object({
  material_id: z.string().min(1, '素材を選択してください'),
  movement_type: z.enum(['adjustment_in', 'adjustment_out']),
  movement_date: z.string().min(1, '日付を入力してください'),
  kg_delta: z.string().min(1, 'kgを入力してください'),
  location: z.string().optional(),
  reason: z.string().min(1, '理由を入力してください'),
});

export type AdjustmentCreateInput = z.infer<typeof adjustmentCreateSchema>;

/** New approval-gated adjustment proposal. */
export const adjustmentProposalSchema = z.object({
  material_id: z.string().min(1, '素材を選択してください'),
  kg_delta: z
    .string()
    .min(1, 'kgを入力してください')
    .refine((v) => v !== '0' && Number(v) !== 0, 'kgは0以外の値を入力してください'),
  reason: z.string().min(1, '理由を入力してください'),
  location: z.string().optional(),
});

export type AdjustmentProposalInput = z.infer<typeof adjustmentProposalSchema>;

/** Filter shape for the 異動明細 tab. */
export interface MovementsFilter {
  material_id?: string;
  type?: 'in' | 'out' | 'adjustment';
  from?: string;
  to?: string;
  source_type?: string;
}
